import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import DelayedPasswordInput from "@/components/DelayedPasswordInput";
import ToniniVisionCTA from "@/components/ToniniVisionCTA";
import NasaHubCTA from "@/components/NasaHubCTA";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-10 overflow-hidden">
      <div className="absolute inset-0 circuit-bg opacity-30" />
      <div className="absolute inset-0 bg-black/85" />
      <div className="absolute inset-0 nasa-grid-bg opacity-50" />

      <div className="relative w-full max-w-md">
        <a href="https://www.myner-adria.com" className="absolute -top-8 left-0 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-[#22ff77] transition-colors">
          ← Početna
        </a>
        <div className="flex flex-col items-center mb-8">
          <img
            src="/logo.png"
            alt="NAŠA Myner Adria"
            data-testid="login-logo"
            style={{mixBlendMode:"screen"}} className="w-44 sm:w-52"
          />
          <h1 className="mt-6 font-display text-4xl sm:text-5xl font-bold tracking-[0.05em] text-center">
            <span className="text-neon-green">NAŠA</span>{" "}
            <span className="text-neon-orange">MYNER</span>
          </h1>
          <div className="mt-3 flex items-center gap-2">
            <span className="h-px w-8 bg-[#ff7a18]/60"></span>
            <p
              data-testid="login-subtitle"
              className="text-[11px] sm:text-xs uppercase tracking-[0.4em] text-[#ff7a18] font-display font-semibold"
            >
              Adria Region — Giveaway strategija
            </p>
            <span className="h-px w-8 bg-[#ff7a18]/60"></span>
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-2 text-center max-w-sm">
            Specifična nagradna igra · Krug po krug · Mašina po mašina
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="login-form"
          className="nasa-card nasa-card-bg-man p-8 space-y-5 backdrop-blur-sm"
        >
          <div>
            <h2 className="font-display text-xl font-semibold tracking-wide">
              Prijava na nalog
            </h2>
            <p className="text-xs text-zinc-500 mt-1">
              Pristupite svojoj kontrolnoj tabli
            </p>
          </div>

          <div className="space-y-1">
            <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Email adresa
            </label>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-black border border-white/10 px-3 py-3 text-sm focus:border-[#22ff77] focus:outline-none transition rounded-sm"
              placeholder="vasa@adresa.com"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              Lozinka
            </label>
            <DelayedPasswordInput
              data-testid="login-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full bg-black border border-white/10 px-3 py-3 text-sm focus:border-[#22ff77] focus:outline-none transition rounded-sm"
              placeholder="••••••••"
            />
          </div>

          {err && (
            <div
              data-testid="login-error"
              className="border border-red-500/40 bg-red-500/10 text-red-300 text-xs px-3 py-2 rounded-sm"
            >
              {err}
            </div>
          )}

          <button
            data-testid="login-submit"
            disabled={loading}
            type="submit"
            className="w-full flex items-center justify-center gap-2 bg-[#22ff77] text-black font-bold py-3 uppercase tracking-[0.2em] text-sm rounded-sm hover:brightness-110 transition-all glow-green disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Prijavi se
          </button>

          <div className="text-center text-xs text-zinc-500 pt-2 border-t border-white/5 space-y-2">
            <div>
              <a href="/zaboravljena-lozinka" data-testid="forgot-link" className="text-zinc-400 hover:text-[#22ff77] text-[11px]">
                Zaboravljena lozinka?
              </a>
            </div>
            <div>
              Nemate nalog?{" "}
              <Link
                to="/registracija"
                data-testid="go-register"
                className="text-[#ff7a18] hover:underline font-semibold"
              >
                Registrujte se
              </Link>
            </div>
          </div>
        </form>

        <p className="text-center text-[10px] text-zinc-600 mt-6 tracking-widest uppercase">
          © {new Date().getFullYear()} NAŠA Myner Adria Region
        </p>

        <ToniniVisionCTA />
        <NasaHubCTA />
      </div>
    </div>
  );
}
